import React, { useEffect, useState, useCallback, useContext } from "react"
import { useHistory, Switch, Route, RouteComponentProps } from "react-router-dom"

import { AuthContext } from "@izettle/react-spa"

import { Error } from "@/components/error"
import { ErrorsList } from "@/components/field/field-errors"
import { baseUrl } from "@/constants"
import { validateFileFormat } from "@/helpers"
import { SelectFilesProps } from "@/models"
import { usePurposes } from "@/providers/purposes"
import { getRequirements, uploadGenericDocuments } from "@/services"
import {
  ViewedPurposeEvent,
  SubmittedPhotoEvent,
  sendToTrackingService,
} from "@/tracking"

import { Upload } from "./upload"

export type RequirementType =
  | "proofOfBusiness"
  | "visaResidentPermit"
  | "proofOfIdUBO"
  | "proofOfBusinessAddress"
  | "contactSupport"

  export type RequirementReasonType =
  | "proofOfBusiness"
  | "proofOfIdUBO"
  | "proofOfId"

export type Requirement = {
  requirementType: RequirementType;
  description : string;
  reason : RequirementReasonType[]
}

export const GenericDocumentUploadView: React.FC<RouteComponentProps> = ({ match }) => {
  const [files, setFiles] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [loadingError, setLoadingError] = useState<boolean>(false)
  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [minFiles, setMinFiles] = useState<number>(-1)
  const [maxFiles, setMaxFiles] = useState<number>(-1)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [fileSuccessfullyAdded, setFileSuccessfullyAdded] = useState(false)
  const [errors, setErrors] = useState<ErrorsList>([])
  const [successfullySubmittedToBackend, setSuccessfullySubmittedToBackend] =
    useState(false)

  const { checkValidPurpose } = usePurposes()

  const history = useHistory()
  const { getToken } = useContext(AuthContext)

  const fetchFileLimit = async () => {
    setLoadingError(false)

    try {
      const requirementsFromApi: any = await getRequirements(getToken)
      setRequirements(requirementsFromApi.requirements)
      setMinFiles(requirementsFromApi.minimumNumberOfDocuments)
      setMaxFiles(requirementsFromApi.maximumNumberOfDocuments)
    } catch {
      setLoadingError(true)
    } finally {
      setIsLoading(false)
    }
  }
  const checkIfPageIsValid = async () => {
    const isValid = await checkValidPurpose("genericDocumentUpload")
    if (!isValid) {
      history.replace(baseUrl)
      return
    }
    void fetchFileLimit()
    const payload = {
      purpose: "genericDocumentUpload",
    }
    sendToTrackingService({ ...ViewedPurposeEvent, payload })
  }

  useEffect(() => {
    void checkIfPageIsValid()
  }, [])

  useEffect(() => {
    // If the user has no files to upload and does not have a requirement to contact support, we should redirect back to the Dockify main page
    if (minFiles === 0 && !requirements.find(requirement => requirement.requirementType === "contactSupport")) { 
      history.push(`${baseUrl}`)
    }
  }, [minFiles])

  useEffect(() => {
    if (!successfullySubmittedToBackend) return

    const timer = setTimeout(() => history.push(baseUrl), 2000)
    return () => clearTimeout(timer)
  }, [successfullySubmittedToBackend])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [isUploading, successfullySubmittedToBackend])

  const addFiles: SelectFilesProps = useCallback(
    (selectedFiles) => {
      if (!validateFileFormat(selectedFiles)) {
        history.push(`${baseUrl}/genericDocumentUpload/error/invalidFileFormat`)
        return Error
      }

      // Transform FileSelect object/array into a normal array of Files
      const transformedFiles = [...files]

      setErrors([])
      for (let i = 0; i < selectedFiles.length; i++) {
        if (!files.find((file) => file.name === selectedFiles[i].name)) {
          transformedFiles.push(selectedFiles[i])
        } else {
          setErrors(["VALIDATION_CANNOT_ADD_SAME_DOCUMENT"])
        }
      }

      setFiles(transformedFiles)
      setFileSuccessfullyAdded(true)
    },
    [files],
  )

  const removeFile = useCallback(
    (name: string) => {
      setErrors([])
      setFiles(files.filter((file) => file.name !== name))
    },
    [files],
  )

  const upload = useCallback(async () => {
    setIsUploading(true)
    setSuccessfullySubmittedToBackend(false)

    try {
      await uploadGenericDocuments(getToken, files)
      setIsUploading(false)
      const payload = {
        purpose: "genericDocumentUpload",
        documentType: "generic",
      }
      const page = "DocumentPurposeDetails"
      sendToTrackingService({ ...SubmittedPhotoEvent, page, payload })
      setSuccessfullySubmittedToBackend(true)
    } catch {
      setIsUploading(false)
      setSuccessfullySubmittedToBackend(false)
      history.push(`${baseUrl}/genericDocumentUpload/error`)
    }
  }, [files])

  return (
    <Switch>
      <Route
        exact
        path={`${match.path}`}
        render={() => (
          <Upload
            files={files}
            isLoading={isLoading}
            loadingError={loadingError}
            isUploading={isUploading}
            addFiles={addFiles}
            removeFile={removeFile}
            upload={upload}
            minFiles={minFiles}
            maxFiles={maxFiles}
            fileSuccessfullyAdded={fileSuccessfullyAdded}
            successfullySubmittedToBackend={successfullySubmittedToBackend}
            uploadErrors={errors}
            requirements={requirements}
          />
        )}
      />
      <Route
        path={`${match.path}/error/:errorType?`}
        render={(props) => (
          <Error
            cancelUrl={`${baseUrl}/genericDocumentUpload`}
            errorType={props.match.params.errorType}
          />
        )}
      />
    </Switch>
  )
}
